import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FaDownload,
  FaWindows,
  FaSyncAlt,
  FaShieldAlt,
  FaDiscord,
  FaCheck,
} from 'react-icons/fa';
import Navbar from '../../components/Navbar/Navbar';
import { fetchSiteConfig, DEFAULT_SITE_CONFIG } from '../../services/siteConfigService';
import './Download.css';

const MANIFEST_URL = '/launcher/latest.json';
const FALLBACK_DOWNLOAD = '/downloads/Nexus-Client-Setup.exe';
const DISCORD_URL = 'https://discord.gg/9W4Vusc4m2';

const Download = () => {
  const [manifest, setManifest] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pages, setPages] = useState(DEFAULT_SITE_CONFIG.pages);

  useEffect(() => {
    let alive = true;
    fetchSiteConfig().then((cfg) => {
      if (alive) setPages(cfg.pages || DEFAULT_SITE_CONFIG.pages);
    });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    fetch(MANIFEST_URL, { cache: 'no-store' })
      .then(async (res) => {
        if (!res.ok) throw new Error('manifest');
        return res.json();
      })
      .then((data) => {
        if (alive) {
          setManifest(data);
          setLoading(false);
        }
      })
      .catch(() => {
        if (alive) {
          setManifest(null);
          setLoading(false);
        }
      });
    return () => {
      alive = false;
    };
  }, []);

  const downloadUrl = manifest?.url || FALLBACK_DOWNLOAD;
  const version = manifest?.version || null;
  const notes = manifest?.notes || null;

  return (
    <div className="dl">
      <Navbar />

      <section className="dl-hero">
        <div className="dl-hero__glow" aria-hidden="true" />
        <div className="dl-hero__grid" aria-hidden="true" />
        <div className="dl-wrap dl-hero__content">
          <p className="dl-hero__kicker">Launcher oficial</p>
          <h1 className="dl-hero__brand">NEXUS</h1>
          <p className="dl-hero__title">Client</p>
          <p className="dl-hero__lead">
            Entre no Nexus Origins com um clique. Contas Microsoft e offline, atualizações
            automáticas e instalação do Java quando precisar.
          </p>

          <div className="dl-hero__actions">
            <a href={downloadUrl} className="dl-btn dl-btn--primary" download>
              <FaDownload aria-hidden="true" />
              {loading ? 'Carregando…' : 'Baixar para Windows'}
            </a>
            <a
              href={DISCORD_URL}
              target="_blank"
              rel="noopener noreferrer"
              className="dl-btn dl-btn--ghost"
            >
              <FaDiscord aria-hidden="true" />
              Discord
            </a>
          </div>

          <p className="dl-hero__meta">
            <FaWindows aria-hidden="true" />
            Windows 10/11
            {version ? ` · v${version}` : ''}
            {' · '}
            Instalador ~6 MB
          </p>
          {notes && <p className="dl-hero__notes">{notes}</p>}
        </div>
      </section>

      <section className="dl-body">
        <div className="dl-wrap">
          <header className="dl-section-head">
            <h2>Por que usar o Client</h2>
            <p>Feito pra Rede Nexus — menos setup, mais jogo.</p>
          </header>

          <ul className="dl-features">
            <li>
              <span className="dl-features__icon" aria-hidden="true">
                <FaCheck />
              </span>
              <div>
                <h3>Entrada rápida</h3>
                <p>Jogue no Origins sem ficar montando perfil no launcher genérico.</p>
              </div>
            </li>
            <li>
              <span className="dl-features__icon" aria-hidden="true">
                <FaSyncAlt />
              </span>
              <div>
                <h3>Atualização automática</h3>
                <p>
                  Quando sair versão nova, o próprio launcher avisa e atualiza — sem baixar o
                  setup no site de novo.
                </p>
              </div>
            </li>
            <li>
              <span className="dl-features__icon" aria-hidden="true">
                <FaShieldAlt />
              </span>
              <div>
                <h3>Contas seguras</h3>
                <p>Login Microsoft (online-mode) ou conta offline para o servidor Nexus.</p>
              </div>
            </li>
          </ul>
        </div>
      </section>

      <section className="dl-steps">
        <div className="dl-wrap">
          <header className="dl-section-head">
            <h2>Como começar</h2>
            <p>Três passos e você já está no spawn.</p>
          </header>
          <ol className="dl-steps__list">
            <li>
              <span>01</span>
              <div>
                <h3>Baixe e instale</h3>
                <p>Rode o instalador do Windows e abra o Nexus Client.</p>
              </div>
            </li>
            <li>
              <span>02</span>
              <div>
                <h3>Entre com sua conta</h3>
                <p>Microsoft ou nick offline — a conta ativa é usada ao jogar.</p>
              </div>
            </li>
            <li>
              <span>03</span>
              <div>
                <h3>Clique em Jogar</h3>
                <p>O Client cuida do Java e conecta no Origins automaticamente.</p>
              </div>
            </li>
          </ol>
        </div>
      </section>

      <section className="dl-cta">
        <div className="dl-wrap dl-cta__inner">
          <h2>Pronto pra jogar?</h2>
          <p>Baixe o launcher oficial e entre na temporada do Nexus.</p>
          <div className="dl-cta__actions">
            <a href={downloadUrl} className="dl-btn dl-btn--primary dl-btn--lg" download>
              <FaDownload aria-hidden="true" />
              Baixar Nexus Client
            </a>
            {pages.vip && (
              <Link to="/vip" className="dl-btn dl-btn--ghost dl-btn--lg">
                Ver planos VIP
              </Link>
            )}
          </div>
        </div>
      </section>

      <footer className="dl-footer">
        <div className="dl-wrap dl-footer__top">
          <div className="dl-footer__brand">
            <p className="dl-footer__name">NEXUS</p>
            <p>Launcher oficial da Rede Nexus / Vih&apos;s Studio.</p>
          </div>
          <div className="dl-footer__cols">
            <div>
              <h4>Links</h4>
              <ul>
                <li><Link to="/">Início</Link></li>
                {pages.vip && <li><Link to="/vip">Planos VIP</Link></li>}
                {pages.team && <li><Link to="/equipe">Equipe</Link></li>}
                <li><Link to="/termos">Termos</Link></li>
              </ul>
            </div>
            <div>
              <h4>Comunidade</h4>
              <ul>
                <li>
                  <a href={DISCORD_URL} target="_blank" rel="noopener noreferrer">
                    Discord
                  </a>
                </li>
              </ul>
            </div>
          </div>
        </div>
        <div className="dl-footer__bottom">
          <p>© 2026 Vih&apos;s Studio. Todos os direitos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default Download;
